import { useEffect, useMemo, useState } from 'react';
import { BarChart3, CheckCircle2, ExternalLink, QrCode } from 'lucide-react';
import { visibleTrusts } from '../lib/accessApi';
import { trusts } from '../lib/trustLibrary';
import { loadPilotEvaluation, savePilotEvaluation, saveNHNNSurveyLinks } from '../lib/trustConfigApi';
import { createQrMatrix } from '../lib/posterExport';
import staffSurveyQr from '../assets/pilot/staff-survey-qr.svg';
import patientSurveyQr from '../assets/pilot/patient-survey-qr.svg';

const KPI_GROUPS = [
  { id: 'understanding', label: 'Understanding', items: [
    ['understanding_score', 'Patient understanding', 'Change in baseline/follow-up patient survey score', 'Patient survey'],
    ['question_confidence', 'Question confidence', 'Patients reporting greater confidence to ask questions', 'Patient survey'],
    ['information_recall', 'Information recall', 'Patients able to recall the relevant role or Journey information', 'Patient survey'],
  ]},
  { id: 'experience', label: 'Experience', items: [
    ['helpfulness', 'Helpfulness', 'Patients rating the role guide or Journey helpful', 'Helpfulness response'],
    ['preparedness', 'Preparedness', 'Patients reporting they feel prepared for the next conversation or step', 'Journey response'],
    ['patient_feedback', 'Patient experience', 'Themes and sentiment from free-text or facilitated feedback', 'Qualitative feedback'],
  ]},
  { id: 'equity', label: 'Equity, accessibility and language', items: [
    ['language_reach', 'Priority-language reach', 'Usage by agreed priority language, with denominator recorded', 'Analytics'],
    ['accessibility_reach', 'Accessibility reach', 'Use of accessibility features and accessible feedback routes', 'Analytics and feedback'],
    ['equity_experience', 'Equity of experience', 'Differences in understanding or helpfulness by agreed cohort', 'Survey and qualitative review'],
  ]},
  { id: 'staff_time', label: 'Staff time', items: [
    ['explanation_time', 'Explanation time', 'Observed or reported minutes spent explaining the same information', 'Time study'],
    ['support_time', 'Support and signposting time', 'Time spent answering avoidable navigation or role-identification questions', 'Staff feedback'],
  ]},
  { id: 'cost', label: 'Printing and communication cost', items: [
    ['printing_change', 'Printing change', 'Change in leaflets, posters or other printed material used', 'Printing record'],
    ['communication_cost', 'Communication cost', 'Change in campaign or communication effort/cost', 'Trust record'],
  ]},
  { id: 'operations', label: 'Operational readiness and manageability', items: [
    ['adoption', 'Adoption', 'Sessions, unique session IDs, role views, searches and Journey starts', 'Weekly analytics'],
    ['content_currency', 'Content currency', 'Selected content and local links remain current and approved', 'Studio audit'],
    ['manageability', 'Manageability', 'Champion and administrator rating of setup, review and support burden', 'Staff survey'],
  ]},
  { id: 'safety', label: 'Safety and guardrails', items: [
    ['defects', 'Defects and incidents', 'Broken links, access issues, content concerns and resolution time', 'Issue log'],
    ['serious_incidents', 'Serious incidents', 'Serious incidents attributable to the service', 'Incident log, target 0'],
  ]},
];

const EVIDENCE = [
  ['baseline', 'Baseline patient and staff surveys recorded'],
  ['follow_up', 'Follow-up/current patient and staff surveys recorded'],
  ['analytics', 'Weekly analytics extract reviewed with denominators'],
  ['time_study', 'Explanation-time observations completed'],
  ['costs', 'Printing and communication records captured'],
  ['qualitative', 'Qualitative feedback themes summarised'],
  ['issues', 'Defects, safety issues and limitations recorded'],
  ['review_dates', 'Early check-in, midpoint and end review dates agreed'],
];

const SURVEYS = [
  { id: 'staff', label: 'Staff Survey', url: 'https://forms.cloud.microsoft/e/k1p7my2Qfq', qr: staffSurveyQr, description: 'For staff feedback on usefulness, implementation and the impact of Know Your Care during the pilot.' },
  { id: 'patient', label: 'Patient Survey', url: 'https://forms.cloud.microsoft/e/eSRzeu0RUq', qr: patientSurveyQr, description: 'For patient feedback on understanding, confidence and experience after using Know Your Care.' },
];

const NHNN_ID = 'nhnn_uclh';
const NHNN_SURVEYS = [
  { id: 'baseline', label: 'NHNN Baseline Understanding Survey', description: 'Before using Know Your Care. Assess understanding of the complex spine surgery and discharge pathways.' },
  { id: 'followUp', label: 'NHNN Follow-up Understanding Survey', description: 'After using Know Your Care. Repeat the same core questions to assess change in understanding.' },
];

function createQrSvgDataUrl(url) {
  if (!url) return '';
  try {
    const matrix = createQrMatrix(url, 'M');
    const size = matrix.getModuleCount();
    let path = '';
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (matrix.isDark(row, col)) path += `M${col + 4} ${row + 4}h1v1h-1z`;
      }
    }
    const fullSize = size + 8;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fullSize} ${fullSize}"><rect width="${fullSize}" height="${fullSize}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  } catch {
    return '';
  }
}

const DECISIONS = ['Continue', 'Adapt', 'Pause', 'Stop'];
const STORAGE_KEY = 'kyc_pilot_evaluation_v1';

function readSaved() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}

export default function PilotEvaluationStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = trusts }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts);
  const initialTrustId = scopedTrusts.find(t => t.id !== 'generic_nhs')?.id || scopedTrusts[0]?.id || 'generic_nhs';
  const [trustId, setTrustId] = useState(initialTrustId);
  const [saved, setSaved] = useState(readSaved);
  const [syncStatus, setSyncStatus] = useState('loading');
  const [syncError, setSyncError] = useState('');
  const [surveyDraft, setSurveyDraft] = useState({ baseline: '', followUp: '' });
  const [surveySaving, setSurveySaving] = useState(false);
  const [surveyNotice, setSurveyNotice] = useState('');
  const current = saved[trustId] || { evidence: {}, decision: '', rationale: '', owner: '', nextReview: '' };

  useEffect(() => {
    let cancelled = false;
    setSyncStatus('loading');
    setSyncError('');
    loadPilotEvaluation(trustId).then(payload => {
      if (cancelled) return;
      const evaluation = payload.evaluation || { evidence: {}, decision: '', rationale: '', owner: '', nextReview: '' };
      const nextSaved = { ...readSaved(), [trustId]: evaluation };
      setSaved(nextSaved);
      setSurveyDraft({
        baseline: evaluation.surveyLinks?.baseline || '',
        followUp: evaluation.surveyLinks?.followUp || '',
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSaved));
      setSyncStatus('cloud');
      setSyncError('');
    }).catch((error) => {
      if (!cancelled) {
        setSyncStatus('local');
        setSyncError(error?.message || 'Cloud sync is unavailable');
      }
    });
    return () => { cancelled = true; };
  }, [trustId]);
  const selectedTrust = scopedTrusts.find(t => t.id === trustId) || null;
  const isNHNN = trustId === NHNN_ID;
  const canManageNHNNSurveys = isNHNN && studioAccess?.roles?.includes('platform_admin');
  const displaySurveys = useMemo(() => isNHNN ? [
    SURVEYS[0],
    ...NHNN_SURVEYS.map(survey => ({
      ...survey,
      url: current.surveyLinks?.[survey.id] || '',
    })),
  ] : SURVEYS, [isNHNN, current.surveyLinks?.baseline, current.surveyLinks?.followUp]);

  const saveSurveyLinks = async () => {
    if (!canManageNHNNSurveys || surveySaving) return;
    setSurveySaving(true);
    setSurveyNotice('');
    try {
      const response = await saveNHNNSurveyLinks(surveyDraft);
      const next = { ...current, surveyLinks: response.surveyLinks };
      const nextSaved = { ...saved, [trustId]: next };
      setSaved(nextSaved);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSaved));
      setSurveyDraft(response.surveyLinks);
      setSurveyNotice('NHNN survey links saved. QR codes now point to the supplied Microsoft Forms.');
    } catch (error) {
      setSurveyNotice(error.message || 'Survey links could not be saved.');
    } finally {
      setSurveySaving(false);
    }
  };
  const kpis = useMemo(() => KPI_GROUPS.flatMap(group => group.items.map(([id, name, measure, evidence]) => ({ id, name, measure, evidence, group: group.label }))), []);
  const completedEvidence = EVIDENCE.filter(([id]) => current.evidence[id]).length;

  const update = patch => {
    const next = { ...current, ...patch };
    const nextSaved = { ...saved, [trustId]: next };
    setSaved(nextSaved);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSaved));
    setSyncStatus('saving');
    setSyncError('');
    savePilotEvaluation(trustId, next).then(() => {
      setSyncStatus('cloud');
      setSyncError('');
    }).catch((error) => {
      setSyncStatus('local');
      setSyncError(error?.message || 'Cloud sync is unavailable');
    });
  };
  const toggleEvidence = id => update({ evidence: { ...current.evidence, [id]: !current.evidence[id] } });

  return <div className="app-shell">
    <header className="topbar">
      <div className="brand"><span className="brand-mark"><BarChart3 /></span><div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>Pilot evaluation</small></div></div>
      <div className="topbar-actions">{scopedTrusts.length > 1 && <label className="context-select"><select value={trustId} onChange={e => setTrustId(e.target.value)}>{scopedTrusts.map(t => <option key={t.id} value={t.id}>{t.shortName}</option>)}</select></label>}</div>
    </header>
    <div className="workspace">
      <aside className="catalogue">{workspaceSwitcher}<div className="catalogue-intro"><p className="eyebrow">Trust evaluation</p><h1>Pilot evaluation</h1><p>Track the evidence agreed for this Trust's pilot without collecting patient-identifiable information.</p><div className="progress"><span style={{ width: `${Math.round((completedEvidence / EVIDENCE.length) * 100)}%` }} /></div><div className="progress-summary"><strong>{completedEvidence}/{EVIDENCE.length} evidence items complete</strong><span>Early check-in · midpoint · end of pilot</span></div></div></aside>
      <main className="review-workspace">
        <div className="role-toolbar"><div><p className="eyebrow">Selected Trust</p><h1>{selectedTrust?.shortName || 'Trust'} · pilot evaluation</h1><div className="meta-row"><span>Evaluation scope and schedule follow the Trust-specific agreement.</span><span title={syncError || undefined}>Evaluation record: {syncStatus === 'cloud' ? 'Cloud synced' : syncStatus === 'saving' ? 'Saving…' : syncStatus === 'loading' ? 'Loading…' : 'Local fallback (not cloud-synced)'}{syncError ? ` · ${syncError}` : ''}</span></div></div></div>
        <section className="trust-config-panel">
          <div className="trust-panel-heading"><div><p className="eyebrow">Pilot feedback</p><h2>Patient and staff surveys</h2><p>{isNHNN ? 'The original staff survey remains available. NHNN baseline and follow-up QR codes appear only after the two Microsoft Forms respondent links are added.' : 'Open the existing Microsoft Forms or download their QR codes.'}</p></div></div>
          {canManageNHNNSurveys && <div className="config-fields" style={{paddingTop:'1rem'}}>
            <p><strong>NHNN survey setup (Platform Admin)</strong> — Paste the respondent links after creating both Forms in Microsoft 365. Leave fields blank until they are ready. These links are specific to NHNN and do not replace the generic patient or staff surveys.</p>
            <label>Baseline survey respondent link<input type="url" value={surveyDraft.baseline} placeholder="https://forms.cloud.microsoft/e/…" onChange={event => setSurveyDraft(draft => ({...draft,baseline:event.target.value}))}/></label>
            <label>Follow-up survey respondent link<input type="url" value={surveyDraft.followUp} placeholder="https://forms.cloud.microsoft/e/…" onChange={event => setSurveyDraft(draft => ({...draft,followUp:event.target.value}))}/></label>
            <div className="trust-actions"><button type="button" className="button primary" disabled={surveySaving || syncStatus === 'loading'} onClick={saveSurveyLinks}>{surveySaving ? 'Saving links…' : 'Save NHNN survey links'}</button></div>
            {surveyNotice && <p role="status">{surveyNotice}</p>}
          </div>}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:'16px',paddingTop:'1rem'}}>{displaySurveys.map(survey => {
            const qr = survey.qr || createQrSvgDataUrl(survey.url);
            return <article key={survey.id} style={{border:'1px solid #d6dee8',borderRadius:'12px',padding:'16px',display:'grid',gap:'12px',alignContent:'start'}}>
              {survey.url && qr
                ? <img src={qr} alt={`QR code for ${survey.label}`} style={{width:180,maxWidth:'100%',height:'auto',background:'#fff',padding:8,borderRadius:8,justifySelf:'center'}}/>
                : <div style={{minHeight:170,display:'grid',placeItems:'center',textAlign:'center',padding:12,background:'#f4f6f8',borderRadius:8}}><strong>Awaiting Microsoft Forms link</strong></div>}
              <div><h3 style={{margin:'0 0 6px'}}>{survey.label}</h3><p style={{margin:0}}>{survey.description}</p></div>
              {survey.url && qr
                ? <div className="trust-actions" style={{justifyContent:'flex-start'}}>
                    <a className="button primary" href={survey.url} target="_blank" rel="noreferrer"><ExternalLink size={16}/>Open survey</a>
                    <a className="button secondary" href={qr} download={`know-your-care-${survey.id}-survey-qr.svg`}><QrCode size={16}/>Download QR</a>
                  </div>
                : <p>QR code unavailable until the live respondent link is saved.</p>}
            </article>;
          })}</div>
        </section>
        <section className="trust-config-panel"><div className="trust-panel-heading"><div><p className="eyebrow">Evidence checklist</p><h2>Are we measuring the right things?</h2><p>Record whether each evidence source has been agreed or collected. Keep sample sizes, denominators and limitations with the review notes.</p></div></div><div className="pilot-checklist"><ol>{EVIDENCE.map(([id, label]) => <li key={id}><label><input type="checkbox" checked={Boolean(current.evidence[id])} onChange={() => toggleEvidence(id)} /><span>{label}</span></label></li>)}</ol></div></section>
        <section className="trust-config-panel"><div className="trust-panel-heading"><div><p className="eyebrow">18-KPI scorecard</p><h2>Outcome measures</h2><p>Seven categories cover understanding, experience, equity, staff time, cost, operations and safety.</p></div></div><div className="readiness-grid">{kpis.map(kpi => <article key={kpi.id} className="ready"><span><CheckCircle2 /></span><div><h2>{kpi.name}</h2><p>{kpi.group}</p><small>{kpi.measure} · Evidence: {kpi.evidence}</small></div></article>)}</div></section>
        <section className="trust-config-panel"><div className="trust-panel-heading"><div><p className="eyebrow">End-of-pilot decision</p><h2>Continue, Adapt, Pause or Stop</h2><p>Record the rationale, owner and next review date. Do not treat missing data as a positive result.</p></div></div><div className="config-fields"><label>Decision<select value={current.decision} onChange={e => update({ decision: e.target.value })}><option value="">Not recorded</option>{DECISIONS.map(decision => <option key={decision} value={decision}>{decision}</option>)}</select></label><label>Decision owner<input value={current.owner} onChange={e => update({ owner: e.target.value })} placeholder="Name or role" /></label><label>Next review date<input type="date" value={current.nextReview} onChange={e => update({ nextReview: e.target.value })} /></label><label>Rationale and conditions<textarea rows="4" value={current.rationale} onChange={e => update({ rationale: e.target.value })} placeholder="Summarise findings, limitations and any conditions before continuation or expansion." /></label></div></section>
        <p className="studio-trade-mark">Do not enter patient-identifiable or confidential information here. Analytics remain session-based and are for service-improvement evaluation.</p>
      </main>
    </div>
  </div>;
}
